import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import {
  FaIcon,
  faXmark,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
} from '@/lib/fa'
import { ProcessedStatus } from '@/app/models/import/processedStatus'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { parseErrors } from '@/modules/imports/ui/ImportErrorsAccordion'
import { formatName } from '@/modules/imports/utils/fileKind'
import { HistoryRow } from './types'

type Props = {
  row: HistoryRow
  onClose: () => void
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const ProcessedStatusBadge: React.FC<{ status: ProcessedStatus }> = ({ status }) => {
  const { t } = useLocalize()
  if (status === ProcessedStatus.All) {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success">
        <FaIcon icon={faCircleCheck} />
        {t('importModule.history-status-compatible')}
      </span>
    )
  }
  if (status === ProcessedStatus.Some) {
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-warning tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-warning">
        <FaIcon icon={faCircleExclamation} />
        {t('importModule.history-status-partial')}
      </span>
    )
  }
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error">
      <FaIcon icon={faCircleXmark} />
      {t('importModule.history-status-incompatible')}
    </span>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="tw-text-sm">
      <label className="tw-mb-0 tw-py-0.5 tw-font-medium">{label}</label>
      <div>{value || '—'}</div>
    </div>
  )
}

export const BasicFileInfoModal: React.FC<Props> = ({ row, onClose }) => {
  const { t } = useLocalize()
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const totalLines = row.validLines + row.invalidLines

  return (
    <ModalLayout>
      <div className="tw-fixed tw-inset-0" onClick={handleBackdropClick}>
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-w-[min(90vw,900px)] tw-max-h-[85vh] tw--translate-x-1/2 tw--translate-y-1/2 tw-flex tw-flex-col tw-rounded-xl tw-bg-base-100 tw-shadow-2xl tw-ring-1 tw-ring-base-300 tw-font-primary">
          {/* Header */}
          <div className="tw-flex tw-flex-none tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-5 tw-py-3">
            <div>
              <p className="tw-m-0 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral">
                {t('importModule.file-info')}
              </p>
              <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content-base tw-truncate tw-max-w-[640px]">
                {row.fileName}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-neutral hover:tw-bg-base-200 hover:tw-text-content-base"
            >
              <FaIcon icon={faXmark} className="tw-h-4 tw-w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-5">
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
              <Row label={t('importModule.fi-import-date')} value={formatDateTime(row.importDate)} />
              <Row label={t('importModule.fi-import-status')} value={<ProcessedStatusBadge status={row.processedStatus} />} />
              <Row label={t('importModule.fi-format')} value={formatName(row.format) || '—'} />
              {row.driver && <Row label={t('importModule.fi-driver')} value={row.driver} />}
              {row.vehicle && <Row label={t('importModule.fi-vehicle')} value={row.vehicle} />}
              <Row label={t('importModule.fi-valid-lines')} value={String(row.validLines)} />
              <Row label={t('importModule.fi-invalid-lines')} value={String(row.invalidLines)} />
              <Row label={t('importModule.fi-total-lines')} value={String(totalLines)} />
              <div className="sm:tw-col-span-2">
                <Row label={t('importModule.fi-file-name')} value={row.fileName} />
              </div>

              {(() => {
                const messages = parseErrors(row.logs ?? undefined)
                if (messages.length === 0) return null
                return (
                  <div className="sm:tw-col-span-2 tw-mt-2">
                    <label className="tw-mb-1 tw-block tw-py-0.5 tw-text-sm tw-font-medium">
                      {t('importModule.fi-logs')}
                    </label>
                    <ol className="tw-m-0 tw-list-none tw-space-y-2 tw-rounded-md tw-border tw-border-base-300 tw-bg-base-100 tw-p-3">
                      {messages.map((msg, i) => (
                        <li key={i} className="tw-flex tw-gap-3 tw-text-sm tw-text-content-base">
                          <span className="tw-inline-flex tw-h-5 tw-min-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-base-200 tw-text-xs tw-font-semibold tw-text-neutral">
                            {i + 1}
                          </span>
                          <span className="tw-break-words">{msg}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}
